import { css } from '@ni/fast-element';
import { White } from '@ni/nimble-tokens/dist/styledictionary/js/tokens';
import { display } from '../../../utilities/style/display';
import {
    applicationBackgroundColor,
    borderHoverColor,
    borderWidth,
    controlHeight,
    fillHoverColor,
    mediumPadding,
    standardPadding,
    tableRowBorderColor
} from '../../../theme-provider/design-tokens';
import { Theme } from '../../../theme-provider/types';
import { hexToRgbaCssColor } from '../../../utilities/style/colors';
import { themeBehavior } from '../../../utilities/style/theme';
import { userSelectNone } from '../../../utilities/style/user-select';
import { styles as expandCollapseStyles } from '../../../patterns/expand-collapse/styles';
import { focusVisible } from '../../../utilities/style/focus';
import { ZIndexLevels } from '../../../utilities/style/types';

export const styles = css`
    ${display('grid')}
    ${expandCollapseStyles}

    :host {
        align-items: center;
        height: calc(${controlHeight} + 2 * ${borderWidth});
        border-top: calc(2 * ${borderWidth}) solid ${applicationBackgroundColor};
        grid-template-columns:
            calc(var(--ni-private-table-group-row-pinned-column-offset))
            calc(
                ${controlHeight} *
                    (var(--ni-private-table-group-row-indent-level) + 1)
            )
            1fr;
    }

    :host([selectable]) {
        grid-template-columns:
            calc(var(--ni-private-table-group-row-pinned-column-offset))
            ${controlHeight}
            calc(
                ${controlHeight} *
                    (var(--ni-private-table-group-row-indent-level) + 1)
            )
            1fr;
    }

    :host::before {
        content: '';
        width: 100%;
        height: ${controlHeight};
        pointer-events: none;
        bottom: 0px;
        position: absolute;
    }

    :host([allow-hover]:hover)::before {
        background-color: ${fillHoverColor};
    }

    :host(${focusVisible}) {
        outline: calc(2 * ${borderWidth}) solid ${borderHoverColor};
        outline-offset: calc(-2 * ${borderWidth});
    }

    :host([has-pinned-columns]) .pinned-column-spacer {
        display: block;
        height: 100%;
        position: sticky;
        left: 0;
        background: ${tableRowBorderColor};
        z-index: ${ZIndexLevels.zIndex1};
    }

    :host([allow-hover][has-pinned-columns]:hover) .pinned-column-spacer {
        background: linear-gradient(${fillHoverColor}, ${fillHoverColor}),
            ${tableRowBorderColor};
    }

    :host([has-pinned-columns]${focusVisible}) .pinned-column-spacer {
        box-shadow: inset 2px -2px 0 ${borderHoverColor};
    }

    .checkbox-container {
        display: flex;
    }

    :host([has-pinned-columns]) .checkbox-container {
        position: relative;
    }

    :host([allow-hover][has-pinned-columns]:hover) .checkbox-container {
        background: linear-gradient(${fillHoverColor}, ${fillHoverColor}),
            ${tableRowBorderColor};
    }

    .expand-collapse-button {
        margin-left: calc(
            ${mediumPadding} + ${standardPadding} * 2 *
                var(--ni-private-table-group-row-indent-level)
        );
    }

    .group-row-header-content {
        display: flex;
        overflow: hidden;
    }

    .group-header-view {
        padding-left: ${mediumPadding};
        ${userSelectNone}
        overflow: hidden;
    }

    .group-row-child-count {
        padding-left: 2px;
        padding-right: ${mediumPadding};
        pointer-events: none;
        ${userSelectNone}
    }

    .selection-checkbox {
        margin-left: ${standardPadding};
    }

    .selection-checkbox::part(label) {
        padding-left: 0px;
    }
`.withBehaviors(
    themeBehavior(
        Theme.color,
        css`
            :host([allow-hover]:hover)::before {
                background-color: ${hexToRgbaCssColor(White, 0.05)};
            }

            :host([has-pinned-columns]) .pinned-column-spacer {
                background: linear-gradient(
                        ${hexToRgbaCssColor(White, 0.1)},
                        ${hexToRgbaCssColor(White, 0.1)}
                    ),
                    ${tableRowBorderColor};
            }

            :host([allow-hover][has-pinned-columns]:hover) .pinned-column-spacer {
                background: linear-gradient(
                        ${hexToRgbaCssColor(White, 0.05)},
                        ${hexToRgbaCssColor(White, 0.05)}
                    ),
                    linear-gradient(
                        ${hexToRgbaCssColor(White, 0.1)},
                        ${hexToRgbaCssColor(White, 0.1)}
                    ),
                    ${tableRowBorderColor};
            }

            :host([has-pinned-columns]) .checkbox-container {
                background: linear-gradient(
                        ${hexToRgbaCssColor(White, 0.1)},
                        ${hexToRgbaCssColor(White, 0.1)}
                    ),
                    ${tableRowBorderColor};
            }

            :host([allow-hover][has-pinned-columns]:hover) .checkbox-container {
                background: linear-gradient(
                        ${hexToRgbaCssColor(White, 0.05)},
                        ${hexToRgbaCssColor(White, 0.05)}
                    ),
                    linear-gradient(
                        ${hexToRgbaCssColor(White, 0.1)},
                        ${hexToRgbaCssColor(White, 0.1)}
                    ),
                    ${tableRowBorderColor};
            }
        `
    ),
    themeBehavior(
        Theme.dark,
        css`
            :host([allow-hover]:hover)::before {
                background-color: ${hexToRgbaCssColor(White, 0.1)};
            }

            :host([allow-hover][has-pinned-columns]:hover) .pinned-column-spacer {
                background: linear-gradient(
                        ${hexToRgbaCssColor(White, 0.1)},
                        ${hexToRgbaCssColor(White, 0.1)}
                    ),
                    ${tableRowBorderColor};
            }

            :host([allow-hover][has-pinned-columns]:hover) .checkbox-container {
                background: linear-gradient(
                        ${hexToRgbaCssColor(White, 0.1)},
                        ${hexToRgbaCssColor(White, 0.1)}
                    ),
                    ${tableRowBorderColor};
            }
        `
    )
);
